import { ConfigurablePropsFromDescription } from '../../../helpers/widget'
import PublishUtils from '../../../helpers/PublishUtils'
import { useRequiredContext } from '../../../utils/use-required-context'
import { createContext } from 'react'
import { IconHelp } from '../IconHelp'
import { TreeButtonsConfig, treeNodeButtonsSchema } from "./config-schema"
import { z } from 'zod'
import { ButtonConfig } from "../../../shared/action-buttons/action-button-schema"
import { JsonTextEditor } from "../../JsonEditor"

export const configOptions = [
  {
    name: 'query',
    type: 'yasgui',
    label: '?uri ?label ?color ?parentUri ?icon ?iconColor ?iconSize ?tooltip ?defaultChecked(xsd:boolean) ?rightLabel ?rightLabelColor ?rightIcon ?rightIconColor ?rightIconSize',
    helpComponent: TreePlusPlusHelp,
    defaultQuery: `
PREFIX e: <https://example-tree.org#>
PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>

SELECT * WHERE {
    GRAPH <https://example-tree.org> {
        ?uri a e:Node ;
            rdfs:label ?label .
        OPTIONAL {
            ?uri e:parentUri ?parentUri .
        }
    }
}
`
  },
  {
    name: 'selectMode',
    type: 'select',
    label: 'Tree selection mode',
    options: [
      { label: 'Disabled', value: 'disabled' },
      { label: 'Single', value: 'single' },
      { label: 'Multiple (use Ctrl and Shift)', value: 'multiple' }
    ],
  },
  {
    name: 'checkboxesEnabled',
    type: 'boolean',
    label: 'Enable checkboxes (this is separate from selection)',
  },
  {
    name: 'checkboxLayout',
    type: 'select',
    label: 'Checkboxes presentation (aligned or nested)',
    options: [
      { label: 'Aligned', value: 'aligned' },
      { label: 'Nested', value: 'nested' },
    ],
  },
  {
    name: 'lastSelectedItemTopic',
    type: 'text',
    label: 'Topic to publish last selected item to'
  },
  {
    name: 'selectedItemsTopic',
    type: 'text',
    label: 'Topic to publish selected items to'
  },
  {
    name: 'checkedItemsTopic',
    type: 'text',
    label: 'Topic to publish checked items to'
  },
  {
    name: 'checkedItemsPublishMode',
    type: 'select',
    label: 'How to publish checked items',
    options: [
      { label: 'All nodes', value: 'all' },
      { label: 'Leaf nodes only', value: 'leaves' },
    ],
  },
  {
    name: 'selectedItemSubscription',
    type: 'text',
    label: 'Listen topic for item selection'
  },
  {
    name: 'selectedItemsSubscription',
    type: 'text',
    label: 'Listen topic for multi item selection. This is based on your multiple value configuration.'
  },
  {
    name: 'checkedItemsSubscription',
    type: 'text',
    label: 'Listen topic for checking items. This is based on your multiple value configuration.'
  },
  {
    name: 'selectedItemRevealMode',
    type: 'select',
    label: 'Behaviour for automatically revealing selected item. Only applies to the single item selection topic.',
    defaultValue: 'disabled',
    options: [
      { label: 'Do not reveal', value: 'disabled' },
      { label: 'Reveal instantly', value: 'instant' },
      { label: 'Reveal smoothly', value: 'smooth' },
    ],
  },
  {
    name: 'editTriplesTemplate',
    type: 'yasgui',
    label: 'EXPERIMENTAL: TriG or SPARQL snippet for built-in edit functionality. Available extra variables: {{node_uri}} {{node_label}} {{node_parentUri}}',
    defaultQuery: `
PREFIX e: <https://example-tree.org#>
PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>

GRAPH <https://example-tree.org> {
  <{{node_uri}}> a e:Node ;
    rdfs:label "{{node_label}}" ;
    e:parentUri <{{node_parentUri}}> .
}`
  },
  {
    name: 'buttonsConfig',
    type: 'json-text',
    label: 'EXPERIMENTAL: Tool buttons JSON configuration (see help for example)',
    helpComponent: ButtonActionsHelp
  },
  {
    name: 'uriModificationEnabled',
    type: 'boolean',
    label: 'EXPERIMENTAL: Allow URI to be modified. If this is disabled, then only the label can be modified.'
  },
  {
    name: 'defaultUriTemplate',
    type: 'text',
    label: 'EXPERIMENTAL: New default URI template for editing functionality. Use {{generated_uuid}} for random UUID'
  },
  PublishUtils.getTimestampParametersDef(),
  PublishUtils.getMultipleValueFormatOptions()
] as const

type Override<T, R> = Omit<T, keyof R> & R

export type RawTreePlusPlusConfigOptions = ConfigurablePropsFromDescription<typeof configOptions>
export type TreePlusPlusConfigOptions = Override<RawTreePlusPlusConfigOptions, {
  buttonsConfig?: TreeButtonsConfig | null,
  sparqlEndpoint: string,
  _parsingErrors: Error[]
}>

export const TreeConfigContext = createContext<TreePlusPlusConfigOptions | null>(null)

export const useTreeConfig = () => useRequiredContext(TreeConfigContext)

function TreePlusPlusHelp() {
  type HelpParam = { name: string, type: string, description?: string }
  const PARAMS: HelpParam[] = [
    { name: '?uri', type: 'URI or literal' },
    { name: '?parentUri', type: 'URI or literal' },
    { name: '?label', type: 'literal' },
    { name: '?color', type: 'literal', description: 'css color' },
    { name: '?icon', type: 'literal' },
    { name: '?iconColor', type: 'literal', description: 'css color' },
    { name: '?iconSize', type: 'literal', description: 'css size unit' },
    { name: '?defaultChecked', type: 'xsd:boolean literal' },
    { name: '?rightLabel', type: 'literal', description: 'optional label at the right size' },
    { name: '?rightLabelColor', type: 'literal', description: 'css color (default is grey)' },
    { name: '?rightIcon', type: 'literal', description: 'default is radix-dot' },
    { name: '?rightIconColor', type: 'literal', description: 'css color' },
    { name: '?rightIconSize', type: 'literal', description: 'css size unit' },
  ] as const
  return (
    <div>
      <table>
        <tbody>
        {PARAMS.map((param, i) => (
          <tr>
            <td>{param.name}</td>
            <td>{param.type}</td>
            <td>{param.description ?? ''}</td>
          </tr>
        ))}
        </tbody>
      </table>

      <IconHelp className="mt-3" />
    </div>

  )
}

function ButtonActionsHelp() {
  const EXAMPLE: TreeButtonsConfig = [
    {
      "icon": "lucide-pencil",
      "label": "Edit node",
      "actions": [
        {
          "type": "start-edit-node",
          "onSuccess": [
            {
              "type": "publish",
              "topic": "lastEditedNode",
              "value": "{{node_uri}}",
              "notification": {
                "onSuccess": "Node edited successfully"
              }
            }
          ],
          "onUriRenamed": [
            {
              "type": "start-rule",
              "startRule": "post-uri-rename-rule",
              "parameters": {
                "prevUri": "{{prevNode_uri}}",
                "nextUri": "{{nextNode_uri}}"
              }
            }
          ]
        }
      ]
    },
    {
      "buttons": [
        {
          "icon": "lucide-plus",
          "label": "Add child",
          "actions": [
            {
              "type": "start-add-node",
              "parent": "{{node_uri}}"
            }
          ]
        },
        {
          "label": "Add sibling",
          "disabled": "{{!node_hasParent}}",
          "actions": [
            {
              "type": "start-add-node",
              "parent": "{{node_parentUri}}"
            }
          ]
        },
        {
          "separator": true
        },
        {
          "icon": "lucide-trash",
          "label": "Delete node",
          "actions": [
            {
              "type": "delete-node",
              "onSuccess": [
                {
                  "type": "start-rule",
                  "startRule": "post-delete-rule",
                  "parameters": {
                    "nodeUri": "{{node_uri}}"
                  }
                }
              ]
            }
          ],
          "disabled": "{{node_hasChildren}}",
          "confirmation": {
            "message": "Are you sure you want to delete this node?"
          }
        }
      ]
    }
  ]
  return (
    <div>
      <h1 className="mb-2">Example action button configuration</h1>
      <JsonTextEditor value={JSON.stringify(EXAMPLE, null, 4)} />
    </div>
  )
}